import type { ToolCall } from '../../../../types'
import { Search, Zap, Send, PenLine, Trash2, Settings, Wrench, type LucideIcon } from 'lucide-react'

export type ToolCallViewData = {
  icon: LucideIcon
  iconUrl: string | null
  name: string
  description: string | null
}

const ICON_BY_PREFIX: Array<[RegExp, LucideIcon]> = [
  [/^(search_|get_|find_|fetch_|list_|read_)/, Search],
  [/^(create_|save_|add_|insert_)/, PenLine],
  [/^(post_|send_|notify_)/, Send],
  [/^(delete_|remove_|destroy_)/, Trash2],
  [/^(run_|execute_|trigger_|dispatch_)/, Zap],
  [/^(manage_|update_|set_|configure_)/, Settings],
]

function fallbackIcon(toolName: string): LucideIcon {
  for (const [re, icon] of ICON_BY_PREFIX) {
    if (re.test(toolName)) return icon
  }
  return Wrench
}

function fallbackName(toolName: string): string {
  return toolName.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}

/** The real tool name inside a wrapper tool's input (`toolSet`/`other`), if any. */
export function extractNestedToolName(toolCall: ToolCall): string | null {
  const input = toolCall.toolInput as Record<string, unknown> | undefined
  if (!input || typeof input !== 'object') return null
  return typeof input.name === 'string' ? input.name : null
}

function resolveDisplayName(toolCall: ToolCall): string {
  const view = toolCall.view
  // If view.name exists and isn't a generic placeholder, use it
  if (view?.name && view.name !== 'other' && view.name !== 'unknown') {
    return view.name
  }
  // Try extracting real tool name from nested input wrappers.
  const nested = extractNestedToolName(toolCall)
  if (nested) return fallbackName(nested)
  // Fall back to toolName
  return fallbackName(toolCall.toolName)
}

// Cache resolved views, keyed on the tool call itself. The store's patch
// layer (`sink-patches.ts`) gives a tool call a new reference exactly when
// its content changed, so identity is the precise cache key — no hand-built
// string to keep in sync with the fields a view reads, and no eviction pass,
// since entries die with the tool calls that own them. Same
// replace-don't-mutate invariant as `toolCallsEqual` (comparators.ts).
const viewCache = new WeakMap<ToolCall, ToolCallViewData>()

export function resolveToolView(toolCall: ToolCall): ToolCallViewData {
  const cached = viewCache.get(toolCall)
  if (cached) {
    return cached
  }

  const view = toolCall.view
  const nestedName = extractNestedToolName(toolCall)
  const result: ToolCallViewData = {
    icon: fallbackIcon(nestedName ?? toolCall.toolName),
    iconUrl: view?.icons?.[0] ?? null,
    name: resolveDisplayName(toolCall),
    description: view?.description ?? null,
  }
  viewCache.set(toolCall, result)

  return result
}
